/** Owner-provided Location Settings information architecture, October 30.
 * Fields are persisted per location. A saved configuration must NOT be
 * construed as activating a workflow that has not yet been wired/tested.
 */
export type LocationSettingKind='text'|'textarea'|'boolean'|'url'|'number'|'email'|'time'|'select';
export type LocationSettingField={key:string;label:string;
  kind:LocationSettingKind;options?:readonly string[]};
export type LocationSettingSection={key:string;title:string;description:string;
  fields:readonly LocationSettingField[]};
export const extraLocationSettingSections=[
 {key:'basicShopInfo',title:'Basic Shop Info',
  description:'Shop name, phone number, email, address, business number, manager info, and IP address',
  fields:[
   {key:'shopName',label:'Shop Name',kind:'text'},
   {key:'phone',label:'Phone Number',kind:'text'},
   {key:'email',label:'Public Email',kind:'email'},
   {key:'address',label:'Address',kind:'textarea'},
   {key:'businessNumber',label:'Business Number',kind:'text'},
   {key:'managerName',label:'Manager Name',kind:'text'},
   {key:'managerEmail',label:'Manager Email',kind:'email'},
   {key:'ipAddress',label:'Shop IP Address',kind:'text'},
  ]},
 {key:'textMessaging',title:'Text Messaging',
  description:'Set when automatic text reminders are sent, Set whether customers are defaulted in to texts or not',
  fields:[
   {key:'reminderHoursAhead',label:'Automatic Text Reminder (hours before appointment)',kind:'number'},
   {key:'secondReminderHoursAhead',label:'Second Text Reminder (hours before appointment)',kind:'number'},
   {key:'defaultCustomersToTexts',label:'Default New Customers In to Texts',kind:'boolean'},
  ]},
 {key:'socialMedia',title:'Social Media',
  description:'Set links for your social media pages (used in emails and elsewhere), Set a description when posting to social media',
  fields:[
   {key:'facebookUrl',label:'Facebook URL',kind:'url'},
   {key:'instagramUrl',label:'Instagram URL',kind:'url'},
   {key:'tiktokUrl',label:'TikTok URL',kind:'url'},
   {key:'youtubeUrl',label:'YouTube URL',kind:'url'},
   {key:'socialShareDescription',label:'Default Social Sharing Description',kind:'textarea'},
  ]},
 {key:'regionalSettings',title:'Regional Settings',
  description:'Time zone, System of measurement, First day of the week, Date format',
  fields:[
   {key:'timezone',label:'Time Zone (IANA e.g. America/New_York)',kind:'text'},
   {key:'measurementSystem',label:'System of Measurement',kind:'select',
    options:['US Customary','Metric']},
   {key:'firstWeekday',label:'First Day of the Week',kind:'select',
    options:['Sunday','Monday','Saturday']},
   {key:'dateFormat',label:'Date Format',kind:'select',
    options:['MM/DD/YYYY','DD/MM/YYYY','YYYY-MM-DD']},
  ]},
 {key:'taxCurrency',title:'Tax & Currency',
  description:'Currency used, Tax rates, Displaying prices with tax included or not, Tax rounding logic, Set if employees can mark sales tax exempt',
  fields:[
   {key:'currency',label:'Currency (ISO Code)',kind:'text'},
   {key:'salesTaxRatePercent',label:'Sales Tax Rate (%)',kind:'number'},
   {key:'taxIncludedPrices',label:'Show Prices With Tax Included',kind:'boolean'},
   {key:'taxRounding',label:'Tax Rounding Logic',kind:'select',
    options:['Per Line Item','On Total']},
   {key:'allowEmployeeTaxExempt',label:'Employees May Mark Sale Tax Exempt',kind:'boolean'},
  ]},
 {key:'timeClocking',title:'Time Clocking',
  description:'Disable time clocking, Set whether staff can clock in from outside the shop or not',
  fields:[
   {key:'disableTimeClock',label:'Disable Time Clocking',kind:'boolean'},
   {key:'allowClockInOffsite',label:'Allow Staff to Clock In From Outside Shop',kind:'boolean'},
  ]},
 {key:'calendarSettings',title:'Calendar Settings',
  description:'Set the display hours and increment settings of various internal and public calendars and schedules',
  fields:[
   {key:'staffDayStart',label:'Internal Calendar Display Start',kind:'time'},
   {key:'staffDayEnd',label:'Internal Calendar Display End',kind:'time'},
   {key:'staffIncrementMinutes',label:'Internal Calendar Display Increment (minutes)',kind:'number'},
   {key:'publicDayStart',label:'Public Calendar Display Start',kind:'time'},
   {key:'publicDayEnd',label:'Public Calendar Display End',kind:'time'},
   {key:'publicIncrementMinutes',label:'Public Calendar Display Increment (minutes)',kind:'number'},
  ]},
 {key:'customerAccounts',title:'Customer Accounts',
  description:'Required fields on customer accounts, Mailing list pitch, "How did you hear about us" options',
  fields:[
   {key:'requireEmail',label:'Require Customer Email',kind:'boolean'},
   {key:'requirePhone',label:'Require Customer Phone Number',kind:'boolean'},
   {key:'requireAddress',label:'Require Customer Address',kind:'boolean'},
   {key:'mailingListPitch',label:'Mailing List Opt-In Pitch',kind:'textarea'},
   {key:'referralOptions',label:'How Did You Hear About Us? (one option per line)',kind:'textarea'},
  ]},
 {key:'internalBooking',title:'Internal Booking',
  description:'Display options for internal schedule, Enable check-ins, Appointment no show time, Invite customer early options',
  fields:[
   {key:'displayMode',label:'Internal Booking Display Mode',kind:'text'},
   {key:'enableCheckins',label:'Enable Check-Ins',kind:'boolean'},
   {key:'noShowAfterMinutes',label:'Mark No-Show After (minutes)',kind:'number'},
   {key:'allowEarlyInvite',label:'Allow Inviting Customer Early',kind:'boolean'},
   {key:'earlyInviteMinutes',label:'Early Invite Advance (minutes)',kind:'number'},
  ]},
 {key:'publicBooking',title:'Public Booking',
  description:'Disable public booking, Choose booking display modes and adjust their settings, Disable filter on public booking, Set limits on when and how customers can book online',
  fields:[
   {key:'disablePublicBooking',label:'Disable Public Booking',kind:'boolean'},
   {key:'displayMode',label:'Public Booking Display Mode',kind:'text'},
   {key:'disableServiceFilter',label:'Disable Service Filter on Public Booking',kind:'boolean'},
   {key:'minimumLeadMinutes',label:'Minimum Advance Booking (minutes)',kind:'number'},
   {key:'maximumAdvanceDays',label:'Maximum Days Ahead',kind:'number'},
   {key:'maximumBookingsPerCustomer',label:'Maximum Future Bookings Per Customer',kind:'number'},
  ]},
 {key:'appointmentConfirmations',title:'Appointment Confirmations',
  description:'Enable call aheads, Set whether customers get asked to confirm in appointment reminders',
  fields:[
   {key:'enableCallAheads',label:'Enable Call-Aheads',kind:'boolean'},
   {key:'askToConfirmInReminders',label:'Ask to Confirm in Appointment Reminders',kind:'boolean'},
  ]},
 {key:'salesLogicReceipts',title:'Sales Logic & Receipts',
  description:'Disable credit card processing, Tips settings, Customer signatures, Service credit upgrade logic, Discount code logic, Receipt text',
  fields:[
   {key:'disableCreditCardProcessing',label:'Disable Credit Card Processing',kind:'boolean'},
   {key:'enableTips',label:'Enable Tips',kind:'boolean'},
   {key:'defaultTipPercent',label:'Default Tip Percent',kind:'number'},
   {key:'requireCustomerSignatures',label:'Require Customer Signatures',kind:'boolean'},
   {key:'allowServiceCreditUpgrades',label:'Allow Service Credit Upgrades',kind:'boolean'},
   {key:'enableDiscountCodes',label:'Enable Discount Codes',kind:'boolean'},
   {key:'receiptFooter',label:'Receipt Text',kind:'textarea'},
  ]},
 {key:'onlinePaymentPolicies',title:'Online Payment Policies',
  description:'Set whether payment is required when customers book online, Return/cancellation policy',
  fields:[
   {key:'requirePaymentForOnlineBooking',label:'Require Payment for Online Bookings',kind:'boolean'},
   {key:'returnPolicy',label:'Return Policy',kind:'textarea'},
   {key:'cancellationPolicy',label:'Cancellation Policy',kind:'textarea'},
  ]},
] as const satisfies readonly LocationSettingSection[];
